import { Popup } from '@kcftech/design-system';
import { SearchTarget } from 'components/global_search/SearchTarget';
import {
    GlobalSearchContainer,
    GlobalSearchContent,
    NoResults,
    SearchResults,
} from 'components/global_search/styled';
import { SearchInput } from 'components/search/SearchInput';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useGlobalSearchHistory } from 'custom_hooks/data_hooks/global_search/useGlobalSearchHistory';
import { useGlobalSearchResults } from 'custom_hooks/data_hooks/global_search/useGlobalSearchResults';
import { useDebounce } from 'custom_hooks/useDebounce';
import { useHighlightedIndex } from 'custom_hooks/useHighlightedIndex';
import { useOnClickOutside } from 'custom_hooks/useOnClickOutside';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    GlobalSearchTargetInfo,
    getSearchTargets,
    parseSearchString,
} from 'utils/global_search';

export type GlobalSearchProps = Readonly<{
    isVisible: boolean;
    onHide: () => void;
}>;

export function GlobalSearch({ isVisible, onHide }: GlobalSearchProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const onHideRef = useRef(onHide);
    onHideRef.current = onHide;
    const inputRef = useRef<HTMLInputElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const history = useHistory();
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText.trim(), 200);

    const { remoteString, remoteTokens, localTokens } = useMemo(
        () => parseSearchString(debouncedSearchText),
        [debouncedSearchText],
    );

    const {
        data: remoteResults,
        isFetching: isLoadingSearchResults,
    } = useGlobalSearchResults({ searchString: remoteString, enabled: isVisible });

    const {
        searchHistory,
        isLoading: isLoadingSearchHistory,
        addToSearchHistory,
        removeFromSearchHistory,
    } = useGlobalSearchHistory();

    const searchResults = useMemo(
        () =>
            remoteResults && isVisible
                ? getSearchTargets(remoteResults, localTokens)
                : undefined,
        [isVisible, localTokens, remoteResults],
    );

    const navigateToTarget = useCallback(
        (result: GlobalSearchTargetInfo) => {
            addToSearchHistory(result);
            onHideRef.current();
            setSearchText('');
            history.push(result.url);
            // Force refresh to have workbench state reload from URL.
            window.location.href = result.url;
        },
        [history, addToSearchHistory],
    );

    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const items = searchResults ?? searchHistory;

    const onHighlightHandleKey = useHighlightedIndex({
        highlightedIndex,
        onChange: setHighlightedIndex,
        items,
        onSelect: navigateToTarget,
    });

    const isLoading = isLoadingSearchResults || isLoadingSearchHistory;

    useOnClickOutside(
        [contentRef],
        () => {
            onHideRef.current();
        },
        isVisible,
    );

    useEffect(() => setSearchText(''), [isVisible]);
    useEffect(() => setHighlightedIndex(0), [items]);

    return isVisible ? (
        <>
            <div ref={containerRef} className="k-overlay" style={{ zIndex: 50000 }} />
            <Popup targetRef={containerRef} focusLock>
                <GlobalSearchContainer aria-label="global search modal">
                    <GlobalSearchContent ref={contentRef} style={{ borderRadius: '5px' }}>
                        <SearchInput
                            autofocus
                            value={searchText}
                            onKeyDown={onHighlightHandleKey}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="Search for corporation, location, system, or asset"
                            ref={inputRef}
                        />
                        <SpinnerArea isLoading={isLoading}>
                            <SearchResults $isLoading={isLoading} className="mt-10">
                                {searchResults
                                    ? searchResults.map((target, index) => (
                                          <SearchTarget
                                              key={target.url}
                                              target={target}
                                              isHighlighted={index === highlightedIndex}
                                              remoteTokens={remoteTokens}
                                              localTokens={localTokens}
                                              onClick={() => navigateToTarget(target)}
                                          />
                                      ))
                                    : searchHistory?.map((target, index) => (
                                          <SearchTarget
                                              key={target.url}
                                              target={target}
                                              isHighlighted={index === highlightedIndex}
                                              onClick={() => navigateToTarget(target)}
                                              onRemove={() =>
                                                  removeFromSearchHistory(target)
                                              }
                                          />
                                      ))}
                                {searchResults && searchResults.length === 0 && (
                                    <NoResults>No Results</NoResults>
                                )}
                            </SearchResults>
                        </SpinnerArea>
                    </GlobalSearchContent>
                </GlobalSearchContainer>
            </Popup>
        </>
    ) : (
        <></>
    );
}
